import { SystemModules } from '@domains/person/user/self/data';
import { PLATFORM_FEATURES, PlatformFeature, EnabledPlatformFeatures, arePlatformFeaturesEnabled, isAnyPlatformFeatureEnabled, hasPlatformFeature } from '@application/config';
import { ERegistrationType } from '../person.enum';

/**
 * Enum for shared person actions used across the application.
 */
export enum ESharedPersonAction {
  EDIT_INFO = 'EDIT_INFO',
  OPEN_WHATSAPP = 'OPEN_WHATSAPP',
  OPEN_CHAT = 'OPEN_CHAT',
  NEW_SUBSCRIPTION = 'NEW_SUBSCRIPTION',
  NEW_SALE = 'NEW_SALE',
  NEW_SERVICE = 'NEW_SERVICE',
  NEW_RECEIPT = 'NEW_RECEIPT',
  OPEN_ACCESS_EDGES = 'OPEN_ACCESS_EDGES',
  NEW_CUSTOMER = 'NEW_CUSTOMER',
  NEW_VISITOR = 'NEW_VISITOR',
  CONVERT_TO_CUSTOMER = 'CONVERT_TO_CUSTOMER',
  CONVERT_TO_SUPPLIER = 'CONVERT_TO_SUPPLIER',
}

/**
 * Interface defining the structure of a shared person action.
 */
export interface ISharedPersonAction {
  id: ESharedPersonAction;
  label: string;
  icon?: string;
  systemIcon?: string;
  tooltipContentDisabled?: string;
  disabled?: boolean;
  onlyTypes?: ERegistrationType[];
  requiredPlatformFeatures?: PlatformFeature[];
  /** OR-semantics: the action is shown when at least one of these features is enabled. */
  requiredAnyPlatformFeatures?: PlatformFeature[];
  requiredModules?: SystemModules[];
  requiredAnyModules?: SystemModules[];
  /**
   * Lifts the module requirements when any of these features is enabled — for a
   * screen that one product reaches through a module and another ships by default.
   */
  modulesExemptPlatformFeatures?: PlatformFeature[];
}

/**
 * Common translation keys and icons for person actions.
 * Centralized here to ensure consistency across search, grid, and profile contexts.
 */
export const sharedPersonActions = (isAdmin?: boolean): ISharedPersonAction[] => [
  {
    id: ESharedPersonAction.EDIT_INFO,
    label: 'SHARED.PERSON_ACTIONS.EDIT_INFO',
    icon: 'fi-rr-user-pen',
  },
  {
    id: ESharedPersonAction.OPEN_WHATSAPP,
    label: 'SHARED.PERSON_ACTIONS.OPEN_WHATSAPP',
    icon: 'fi-brands-whatsapp',
    tooltipContentDisabled: 'COMPONENTS.WHATSAPP_DISABLED_NO_PHONES',
    requiredModules: [SystemModules.Whatsapp],
  },
  {
    id: ESharedPersonAction.OPEN_CHAT,
    label: 'SHARED.PERSON_ACTIONS.OPEN_CHAT',
    icon: 'fi-rr-messages',
    onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor, ERegistrationType.User],
    requiredAnyPlatformFeatures: [PLATFORM_FEATURES.treino, PLATFORM_FEATURES.school],
    requiredModules: [SystemModules.TreiNo],
    modulesExemptPlatformFeatures: [PLATFORM_FEATURES.school],
  },
  {
    id: ESharedPersonAction.NEW_SUBSCRIPTION,
    label: 'SHARED.PERSON_ACTIONS.NEW_SUBSCRIPTION',
    icon: 'fi-rr-memo-circle-check',
    onlyTypes: isAdmin ? [ERegistrationType.Customer, ERegistrationType.Visitor] : [ERegistrationType.Customer],
    requiredModules: [SystemModules.Assinaturas],
  },
  {
    id: ESharedPersonAction.NEW_SALE,
    label: 'SHARED.PERSON_ACTIONS.NEW_SALE',
    icon: 'fi-rr-box-open',
    onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor, ERegistrationType.User],
    requiredPlatformFeatures: [PLATFORM_FEATURES.goods],
    requiredModules: [SystemModules.Mercadorias],
  },
  {
    id: ESharedPersonAction.NEW_SERVICE,
    label: 'SHARED.PERSON_ACTIONS.NEW_SERVICE',
    icon: 'fi-rr-heart-partner-handshake',
    onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor, ERegistrationType.User],
    requiredPlatformFeatures: [PLATFORM_FEATURES.workServices],
    requiredModules: [SystemModules.Servicos],
  },
  {
    id: ESharedPersonAction.NEW_RECEIPT,
    label: 'SHARED.PERSON_ACTIONS.NEW_RECEIPT',
    icon: 'fi-rr-bill-arrow',
    onlyTypes: [ERegistrationType.Customer],
    requiredAnyModules: [SystemModules.Assinaturas, SystemModules.Mercadorias, SystemModules.Servicos],
  },
  {
    id: ESharedPersonAction.CONVERT_TO_CUSTOMER,
    label: 'SHARED.PERSON_ACTIONS.CONVERT_TO_CUSTOMER',
    icon: 'fi-rr-user-add',
    onlyTypes: [ERegistrationType.Visitor],
  },
  {
    id: ESharedPersonAction.CONVERT_TO_SUPPLIER,
    label: 'SHARED.PERSON_ACTIONS.CONVERT_TO_SUPPLIER',
    icon: 'fi-rr-user-add',
    onlyTypes: [ERegistrationType.Contact],
  },
  {
    id: ESharedPersonAction.OPEN_ACCESS_EDGES,
    label: null,
    systemIcon: 'toletus-logo',
    onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor, ERegistrationType.User],
    requiredModules: [SystemModules.Acesso],
  },
];

/**
 * Maps string-based RegistrationType values to the numeric ERegistrationType enum.
 */
const registrationTypeStringToEnum: Record<string, ERegistrationType> = {
  Customer: ERegistrationType.Customer,
  Visitor: ERegistrationType.Visitor,
  User: ERegistrationType.User,
  Supplier: ERegistrationType.Supplier,
  Contact: ERegistrationType.Contact,
  Helpdesk: ERegistrationType.Helpdesk,
};

/**
 * Normalizes a registration type value (string or numeric) to ERegistrationType.
 */
function normalizeRegistrationType(value: string | number): ERegistrationType | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  if (typeof value === 'number') return value as ERegistrationType;
  if (!isNaN(Number(value))) return Number(value) as ERegistrationType;
  return registrationTypeStringToEnum[value];
}

/**
 * Whether this tenant is exempt from the action's module requirements — see
 * `modulesExemptPlatformFeatures`. Shared so the flower menu, the search list and
 * the search card all resolve the gate the same way.
 */
export const isModuleGateExempt = (
  enabledPlatformFeatures: EnabledPlatformFeatures | null | undefined,
  action: Pick<ISharedPersonAction, 'modulesExemptPlatformFeatures'>
): boolean =>
  !!action.modulesExemptPlatformFeatures?.some((feature) =>
    hasPlatformFeature(enabledPlatformFeatures, feature)
  );

/**
 * Filters shared person actions based on a given registration type and platform features.
 * Accepts both ERegistrationType (numeric) and RegistrationType (string).
 * Actions without `onlyTypes` or `requiredPlatformFeatures` are always shown unless restricted by the other.
 */
export function filterSharedPersonActions(
  actions: ISharedPersonAction[],
  {
    registrationType,
    enabledPlatformFeatures,
    isModuleAllowed,
  }: {
    registrationType?: ERegistrationType | string;
    enabledPlatformFeatures?: EnabledPlatformFeatures | null;
    isModuleAllowed?: (module: SystemModules) => boolean;
  }
): ISharedPersonAction[] {
  const normalized = registrationType != null ? normalizeRegistrationType(registrationType) : undefined;

  return actions.filter((action) => {
    const hasOnlyTypes = !action?.onlyTypes || (normalized != null && action.onlyTypes.includes(normalized));
    const hasPlatformFeatures = arePlatformFeaturesEnabled(enabledPlatformFeatures, action.requiredPlatformFeatures);
    const hasAnyPlatformFeatures = isAnyPlatformFeatureEnabled(enabledPlatformFeatures, action.requiredAnyPlatformFeatures);
    const modulesExempt = isModuleGateExempt(enabledPlatformFeatures, action);
    const hasRequiredModules =
      modulesExempt ||
      !action?.requiredModules || (isModuleAllowed != null && action.requiredModules.every((m) => isModuleAllowed(m)));
    const hasRequiredAnyModules =
      modulesExempt ||
      !action?.requiredAnyModules || (isModuleAllowed != null && action.requiredAnyModules.some((m) => isModuleAllowed(m)));

    return hasOnlyTypes && hasPlatformFeatures && hasAnyPlatformFeatures && hasRequiredModules && hasRequiredAnyModules;
  });
}

// Keeping it for backward compatibility but it should be replaced by filterSharedPersonActions
export const filterSharedPersonActionsByType = (
  actions: ISharedPersonAction[],
  registrationType?: ERegistrationType | string
): ISharedPersonAction[] => filterSharedPersonActions(actions, { registrationType });
